import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compact, flatMap, get } from 'lodash-es';
import dateFormat from 'dateformat';
import { Link } from 'site-modules/shared/components/link/link';
import { InventoryEntities } from 'client/data/models/inventory';
import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { isPartnerDealer, isPrivateParty } from 'site-modules/shared/utils/dealer-details-utils';
import { adjustTimezone } from 'site-modules/shared/utils/date-utils';
import { getDeliveryTypeTitle } from 'site-modules/shared/components/delivery/helper';
import { DATA_PATH } from 'client/engagement-handlers/inventory-engagement-handler/constants';

import './vdp-disclaimer.scss';

export function VisitorsAgreementLink() {
  return (
    <Link to="/about/visitor-agreement.html" className="text-underline">
      Edmunds.com Visitor&#39;s Agreement
    </Link>
  );
}

function buildOfferDisclaimerText({ vinData, withPublishDate }) {
  const publishDate = get(vinData, 'publishDate');
  const offerStatusText = publishDate
    ? `This vehicle is offered as of ${dateFormat(
        adjustTimezone(new Date(publishDate)),
        'mm/dd/yyyy'
      )} and is offered subject to prior sale;`
    : 'This vehicle is offered subject to prior sale;';

  return {
    offerStatusText,
    offerDisclaimerText: withPublishDate
      ? `${offerStatusText} accordingly, there is no guarantee that this vehicle will not be sold to another buyer. Edmunds makes every effort to provide accurate information, but please verify with the dealer before purchasing.`
      : 'This vehicle is offered subject to prior sale; accordingly, there is no guarantee that this vehicle will not be sold to another buyer. Offer expires on the close of business on the date set forth above.',
  };
}

function buildNewVinDisclaimer({ vinData, withPublishDate }) {
  const { offerDisclaimerText } = buildOfferDisclaimerText({ vinData, withPublishDate });
  const purchasePromiseDisclaimer = get(vinData, 'prices.pricePromise.purchasePromiseDisclaimer', '');

  return compact([
    {
      id: 'msrp',
      text: "MSRP is the Manufacturer's Suggested Retail Price for this vehicle as provided to Edmunds by the dealer.",
    },
    purchasePromiseDisclaimer && {
      id: 'purchase-promise',
      className: 'purchase-disclaimer',
      text: purchasePromiseDisclaimer,
    },
    {
      id: 'offer-disclaimer',
      text: offerDisclaimerText,
    },
  ]);
}

function buildUsedVinDisclaimer({ vinData, withPublishDate, isVDP }) {
  const { offerStatusText, offerDisclaimerText } = buildOfferDisclaimerText({ vinData, withPublishDate });
  const purchasePromiseDisclaimer = get(vinData, 'prices.usedPlus.purchasePromiseDisclaimer', '');
  const dealerUsedDisclaimer = get(vinData, 'dealerInfo.disclaimers.usedCustomDisclaimer');
  const isPrivatePartyListing = isPrivateParty(vinData);
  const isPartner = isPartnerDealer(vinData);
  const dealerDisclaimer = isPartner ? purchasePromiseDisclaimer : dealerUsedDisclaimer;
  const usedVehicleDisclaimer = isVDP
    ? `${offerStatusText} accordingly, there is no guarantee that this vehicle will not be sold to another buyer. ${isPrivatePartyListing ? '' : 'Edmunds makes every effort to provide accurate information, but please verify with the dealer before purchasing.'}`
    : `${offerDisclaimerText} Dealer is under no obligation to include or offer any additional equipment, products or services.`;

  return compact([
    {
      id: 'used-vehicle-disclaimer',
      text: usedVehicleDisclaimer,
    },
    dealerDisclaimer && {
      id: 'dealer-disclaimer',
      text: isPartner ? dealerDisclaimer : `Dealer Disclaimer: ${dealerDisclaimer}`,
    },
  ]);
}

function buildCommonDisclaimer(vinData) {
  const hasDeliveryType = !!getDeliveryTypeTitle(vinData);
  const stockPhoto = get(vinData, DATA_PATH.STOCK_PHOTO_URL);

  return compact([
    hasDeliveryType && {
      id: 'delivery-disclaimer',
      marker: '‡',
      text: 'Delivery does not apply to Alaska and Hawaii. Contact the dealer for delivery details, restrictions and costs.',
    },
    stockPhoto && {
      id: 'stock-photo-disclaimer',
      marker: '**',
      text: 'This image is a stock photo and may not reflect the model year or color of, and/or equipment offered on, the advertised vehicle.',
    },
    {
      id: 'visitor-agreement',
      text: (
        <>
          This listing is displayed subject to the&nbsp;
          <VisitorsAgreementLink />.
        </>
      ),
    },
  ]);
}

function buildDisclaimer({ vinData, withPublishDate, isVDP }) {
  const type = get(vinData, 'type', '');

  return flatMap(
    compact([
      type && isNew(type) && buildNewVinDisclaimer({ vinData, withPublishDate }),
      type && isUsed(type) && buildUsedVinDisclaimer({ vinData, withPublishDate, isVDP }),
      buildCommonDisclaimer(vinData),
    ])
  );
}

export function VdpDisclaimer({ vinData = {}, textClassName = 'small', withPublishDate = false, isVDP = false }) {
  const disclaimers = buildDisclaimer({ vinData, withPublishDate, isVDP });

  return (
    <div className="text-gray-darker vdp-bottom-disclaimer pt-1_5 pb-0_5">
      {disclaimers.map(({ id, className, marker, text }) => (
        <p className={classnames(className, textClassName)} key={id}>
          <>
            {marker && <sup>{marker}</sup>}
            {marker ? ` ${text}` : text}
          </>
        </p>
      ))}
    </div>
  );
}

VdpDisclaimer.propTypes = {
  vinData: InventoryEntities.InventoryVin,
  textClassName: PropTypes.string,
  withPublishDate: PropTypes.bool,
  isVDP: PropTypes.bool,
};
